import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Download, Camera } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { VisitingCard } from "@/components/VisitingCard";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [pub, setPub] = useState(user?.public_leaderboard ?? true);
  const [photo, setPhoto] = useState(user?.profile_photo || "");
  const fileRef = useRef();

  const save = async () => {
    try {
      const { data } = await api.put("/profile", { name, public_leaderboard: pub, profile_photo: photo });
      setUser(data);
      toast.success("Profile updated");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("Photo must be under 1 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0B223D]">My Profile</h1>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <div className="flex items-center gap-4">
          {photo ? <img src={photo} alt="profile" className="w-20 h-20 rounded-full object-cover border-2 border-teal-500" data-testid="profile-photo" />
            : <div className="w-20 h-20 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center font-display font-extrabold text-3xl">{(name || "A").charAt(0).toUpperCase()}</div>}
          <div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickPhoto} data-testid="profile-photo-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="profile-photo-btn" className="font-semibold"><Camera className="w-4 h-4 mr-1.5" /> Upload photo</Button>
            <p className="text-xs text-slate-400 mt-1.5">JPG/PNG under 1 MB — used on your visiting card.</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="font-semibold text-slate-600">Permanent User ID</Label><Input readOnly value={user?.user_code} className="mt-1.5 h-11 font-mono-num bg-slate-50" data-testid="profile-user-code" /></div>
          <div><Label className="font-semibold text-slate-600">Email</Label><Input readOnly value={user?.email} className="mt-1.5 h-11 bg-slate-50" /></div>
        </div>
        <div><Label className="font-semibold text-slate-600">Full Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" className="mt-1.5 h-11" /></div>
        <div><Label className="font-semibold text-slate-600">Mobile</Label><Input readOnly value={user?.mobile} className="mt-1.5 h-11 bg-slate-50" /></div>
        <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
          <div><p className="font-semibold text-[#0B223D]">Show me on public leaderboard</p><p className="text-sm text-slate-400">Display your name on Top Performers.</p></div>
          <Switch checked={pub} onCheckedChange={setPub} data-testid="profile-public-toggle" />
        </div>
        <Button onClick={save} data-testid="profile-save-btn" className="bg-[#0B223D] hover:bg-[#06182B] text-white font-bold h-11 rounded-xl px-6">Save changes</Button>
      </div>

      <div>
        <h2 className="font-display text-xl font-extrabold text-[#0B223D] mb-1">My Adviser Visiting Card</h2>
        <p className="text-sm text-slate-500 mb-4">Share this digital card with clients. Details auto-filled from your profile.</p>
        <div className="flex justify-center bg-slate-100 rounded-2xl p-6">
          <VisitingCard user={{ ...user, name }} photo={photo} />
        </div>
      </div>
    </div>
  );
}
